import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { AppConfigService } from '../../app.config';
import { ApiError } from '../models/error';
import { BasicBill, Bill } from '../types/bills/Bill';
import { BillVersion } from '../types/bills/BillVersion';
import { CodeSection } from '../types/bills/CodeSection';
import { CachedHttpService } from './cached-http.service';
import { AppConfig } from '../models/appConfig';
import { Legislator } from '../types/bills/Legislator';

@Injectable({
    providedIn: 'root',
})
export class BillService {
    baseLegUrl: string;
    config: AppConfig;
    headers = {
        Authorization: '1234',
    };

    /*
     * Service Constructor
     */
    constructor(
        config: AppConfigService,
        cachedHttp: CachedHttpService,
        private _http: HttpClient,
    ) {
        this.config = config.getConfig();
        this.baseLegUrl = `${this.config.microServiceUrl}legislative`;
    }

    /*
     * Get Bill by Id
     */
    getBill(id: string): Observable<Bill> {
        const url = `${this.baseLegUrl}/bills/${id}`;

        return this._http
            .get<Bill>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /*
     * Get Version Text by Version Id
     */
    getBillVersionHtml(id: string): Observable<BillVersion> {
        const url = `${this.baseLegUrl}/bills/version/${id}/html`;

        return this._http
            .get<BillVersion>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /*
     * Search Bill Digest Text by Bill Id
     */
    getBillDigest(id: string): Observable<string> {
        const url = `${this.baseLegUrl}/bills/${id}/digest`;

        return this._http
            .get<string>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /*
     * Get Affected Code Sections for Bill Id
     */
    getCodeSections(id: string): Observable<CodeSection[]> {
        const url = `${this.baseLegUrl}/laws/${id}`;

        return this._http
            .get<CodeSection[]>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /*
     * Download Committee Analysis for a Bill by Analysis Id
     */
    downloadAnalysis(id: number) {
        const url = `${this.baseLegUrl}/bills/analysis/${id}`;

        return this._http
            .get(url, {
                reportProgress: true,
                observe: 'events',
                responseType: 'blob',
            })
            .pipe(catchError((error) => this.handleError(error)));
    }

    /*
     * Search Bill By Measure Number across sessions
     */
    searchBills(text: string): Observable<BasicBill[]> {
        const url = `${this.baseLegUrl}/bills/search?text=${text}`;

        return this._http
            .get<BasicBill[]>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /*
     * Search Bill By Measure Number or Subject Text for selected session
     */
    searchBillsBySession(
        text: string,
        session: string,
    ): Observable<BasicBill[]> {
        const url = `${this.baseLegUrl}/bills/search/${session}/${text}`;

        return this._http
            .get<BasicBill[]>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    getAuthors(): Observable<Legislator[]> {
        const url = `${this.baseLegUrl}/bills/authors`;

        return this._http
            .get<Legislator[]>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    getVersions(): Observable<string[]> {
        const url = `${this.baseLegUrl}/bills/versions`;

        return this._http
            .get<string[]>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    testApi(url: string): Observable<any> {
        url =
            url === ''
                ? `${this.config.apiUrl}${this.config.defaultTestUrl}`
                : `${this.config.apiUrl}${url}`;
        return this._http.get<any>(url, {
            // We have to add the auth header to get past the ApiService for now
            // When MsalInterceptor is activiated it will be replaced with an actual access token
            headers: this.headers,
        });
    }

    private handleError(error: ApiError) {
        return throwError(() => new ApiError('An unexpected error occurred'));
    }
}
